import React, { useState } from "react";
import { ArrowRight, ArrowUp, ArrowDown } from "@phosphor-icons/react";

export const inr0 = (v) => `₹${Math.round(Number(v || 0)).toLocaleString("en-IN")}`;
const lakh = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : inr0(v));

export function Money({ value, className = "" }) {
  const [r, p] = Number(value || 0).toFixed(2).split(".");
  return (
    <span className={className}>
      ₹{Number(r).toLocaleString("en-IN")}
      {p !== "00" && <span className="text-[0.6em] text-slate-500 font-medium">.{p}</span>}
    </span>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex p-1 rounded-sm bg-slate-700/50 border border-slate-700/60" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={"px-4 py-1.5 text-sm rounded-sm transition-colors " +
            (value === o.value ? "bg-slate-800 text-slate-50 font-medium shadow-sm" : "text-slate-400 hover:text-slate-200")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function KpiCard({ label, children, foot, onClick, testid }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} data-testid={testid}
      className={"surface p-5 min-w-0 " + (onClick ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : "")}>
      <div className="text-sm text-slate-400">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-slate-50 tracking-tight truncate">{children}</div>
      {foot && <div className="mt-2 text-sm text-slate-400 truncate">{foot}</div>}
    </div>
  );
}

export function Delta({ pct, vs }) {
  if (pct === undefined || pct === null || !Number.isFinite(pct) || Math.abs(pct) > 999) return null;
  const up = pct >= 0;
  return (
    <span className={up ? "text-emerald-600" : "text-red-600"} data-testid="received-delta">
      {up ? <ArrowUp size={12} className="inline -mt-0.5" /> : <ArrowDown size={12} className="inline -mt-0.5" />} {Math.abs(Math.round(pct))}% vs {vs}
    </span>
  );
}

const PIPE = [
  { key: "new_order", label: "New order", dot: "bg-orange-500" },
  { key: "design_pending", label: "Design pending", dot: "bg-violet-500" },
  { key: "ready_to_cut", label: "Ready to cut", dot: "bg-teal-600" },
  { key: "in_cutting", label: "In cutting", dot: "bg-blue-500" },
  { key: "completed", label: "Ready for delivery", dot: "bg-emerald-600" },
  { key: "delivered", label: "Delivered", dot: "bg-slate-500" },
];

export function Pipeline({ counts, onStageClick }) {
  return (
    <div className="surface p-6" data-testid="home-pipeline">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-100">Pipeline</div>
        <button onClick={() => onStageClick("all")} className="text-sm text-slate-400 hover:text-slate-100" data-testid="pipeline-all-orders">
          All orders <ArrowRight size={12} className="inline" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-4">
        {PIPE.map((p) => (
          <button key={p.key} onClick={() => onStageClick(p.key)} data-testid={`pipeline-${p.key}`}
            className="text-left px-5 border-l border-slate-700/70 hover:bg-slate-700/20 transition-colors py-1">
            <div className="text-2xl font-semibold text-slate-50">{(counts?.[p.key] || 0).toLocaleString("en-IN")}</div>
            <div className="mt-1 text-sm text-slate-400 flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${p.dot}`} />{p.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

const DEPT_OPTS = [{ value: "sheet_cutting", label: "Sheet cutting" }, { value: "gate_railing", label: "Gates & railings" }];

export function SalesCard({ title, timelines, onBucketClick }) {
  const [dept, setDept] = useState("sheet_cutting");
  const data = timelines?.[dept];
  const other = timelines?.[dept === "sheet_cutting" ? "gate_railing" : "sheet_cutting"];
  const buckets = data?.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.sales || 0));
  const best = buckets.reduce((a, b) => ((b.sales || 0) > (a?.sales || 0) ? b : a), null);
  const ticks = [0, Math.floor(buckets.length * 0.25), Math.floor(buckets.length * 0.5), Math.floor(buckets.length * 0.75)];
  const otherLabel = DEPT_OPTS.find((d) => d.value !== dept).label;
  return (
    <div className="surface p-6 min-w-0" data-testid={`sales-chart-${dept}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-slate-400">{title}</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-slate-50 tracking-tight" data-testid={`sales-total-${dept}`}>{inr0(data?.total_sales)}</span>
            <span className="text-sm text-slate-400">{data?.total_orders ?? 0} orders</span>
          </div>
        </div>
        <Segmented options={DEPT_OPTS} value={dept} onChange={setDept} testid="sales-dept" />
      </div>
      <div className="mt-6 h-56 flex items-end gap-[3px] border-b border-slate-700/70" data-testid="revenue-timeline-chart">
        {buckets.length === 0 && <div className="m-auto text-sm text-slate-500">Is range me abhi data nahi hai</div>}
        {buckets.map((b, i) => (
          <button key={i} type="button" title={`${b.date || b.label} · ${inr0(b.sales)}`}
            onClick={() => onBucketClick?.({ ...b, project_type: dept, dept_label: DEPT_OPTS.find((d) => d.value === dept).label })}
            className={"flex-1 min-w-0 rounded-t-[3px] transition-opacity hover:opacity-80 " + (i === buckets.length - 1 ? "bg-orange-500" : "bg-slate-700")}
            style={{ height: `${Math.max(((b.sales || 0) / max) * 100, b.sales ? 2 : 0.5)}%` }}
            data-testid={`sales-bar-${i}`} />
        ))}
      </div>
      {buckets.length > 0 && (
        <div className="mt-2 flex justify-between text-xs text-slate-500">
          {ticks.map((t) => <span key={t}>{buckets[t]?.label}</span>)}<span>Today</span>
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-slate-700/70 flex flex-wrap gap-x-8 gap-y-1 text-sm text-slate-400">
        {best && best.sales > 0 && <span>Best day <b className="text-slate-100 font-medium">{best.label} · {lakh(best.sales)}</b></span>}
        <span>{otherLabel} <b className="text-slate-100 font-medium">{inr0(other?.total_sales)} · {other?.total_orders ?? 0} orders</b></span>
      </div>
    </div>
  );
}

export function CollectionsCard({ today }) {
  const all = today?.collections_by_mode || [];
  const paid = all.filter((m) => m.total > 0);
  const rows = [...paid, ...all.filter((m) => !(m.total > 0))].slice(0, Math.max(paid.length, 5));
  return (
    <div className="surface p-6" data-testid="home-collections">
      <div className="font-semibold text-slate-100 mb-2">Today&apos;s collections</div>
      {rows.length === 0 && <div className="text-sm text-slate-500 py-2">Aaj abhi koi collection nahi</div>}
      {rows.map((m) => (
        <div key={m.key} className="flex justify-between py-3 border-b border-slate-700/70 text-[15px]">
          <span className="text-slate-300">{m.label === "advance_credit" ? "Advance credit" : m.label}</span>
          <span className={m.total ? "text-slate-100" : "text-slate-500"}>{inr0(m.total)}</span>
        </div>
      ))}
      <div className="flex justify-between pt-4 font-semibold text-slate-50">
        <span>Total</span><Money value={today?.collections?.total} />
      </div>
    </div>
  );
}

export function MachineCard({ today, onOpenOrder }) {
  const list = (today?.in_cutting || []).slice(0, 6);
  return (
    <div className="surface p-6" data-testid="home-on-machine">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate-100">On the machine</div>
        <span className="text-sm text-blue-500 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" />{today?.in_cutting_count ?? 0} cutting</span>
      </div>
      {list.length === 0 && <div className="text-sm text-slate-500 py-2">Machine pe abhi koi job nahi</div>}
      {list.map((o) => (
        <button key={o.id} onClick={() => onOpenOrder(o.id)} data-testid={`widget-cutting-${o.order_number}`}
          className="w-full flex items-center justify-between py-3 border-b border-slate-700/70 last:border-0 text-left group">
          <div className="min-w-0">
            <div className="text-[15px] text-slate-100 truncate">{o.customer_name}</div>
            <div className="text-xs text-slate-500 font-mono">{o.order_number}</div>
          </div>
          <ArrowRight size={14} className="text-slate-500 group-hover:translate-x-0.5 transition-transform" />
        </button>
      ))}
    </div>
  );
}
